import React, { useState } from 'react';
import { Button, Message, Modal } from '@bedrock/components';
import type { FormInstance } from '@bedrock/components/lib/Form/util';
import { refundConfirm, refundAbandon } from '../../api';
import { useRefundSendBackModal } from './utils';
import { Z_INDEX } from '../../../../utils/zIndex';
import type { PermitActionVo } from 'services/api';

export interface RefundActionsProps {
  orderId: number | null;
  /** 操作权限（确认/退回/废弃按钮显隐，后端详情接口返回，按当前用户是否待办人 + 状态节点计算）。
   *  镜像充值 RechargeConfirmView 的 detail.permit_action。 */
  permitAction?: PermitActionVo;
  onRefresh?: () => void;
  /** 确认表单实例：供应商确认（FR-13）读退款说明/附件 */
  form?: FormInstance;
}

/**
 * 退款单操作区（确认/退回/废弃）
 *
 * 设计依据：frontend-design.md §5 确认页 + PrePayment `Drawer/Actions` 范式。
 * 3 角色（策划/优化师/供应商）确认走统一 `refundConfirm`，仅供应商附件差异（FR-13）
 * 经 status 判定，故以统一 RefundActions 取代 3 角色子目录空占位（OptimizerConfirms/
 * PlannerConfirms/SupplierConfirms）。
 * UX 反馈 2026-08-05：三按钮 minWidth 120 + 退回弹框 width 680 按钮自适应（useRefundSendBackModal）。
 */
export const RefundActions: React.FC<RefundActionsProps> = ({
  orderId,
  permitAction,
  onRefresh,
  form,
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const canConfirm = permitAction?.can_confirm === true;
  const canReturn = permitAction?.can_return === true;
  const canAbandon = permitAction?.can_abandon === true;
  const sendBack = useRefundSendBackModal({ orderId, onSuccess: onRefresh });

  const handleConfirm = async () => {
    if (!orderId) return;
    // 供应商确认（FR-13）上传退款说明+附件：从 form 读扁平字段 refund_remark/refund_attachments（textWithUpload 投影）
    const refundRemark = form?.getFieldValue('refund_remark') as
      | string
      | undefined;
    const refundAttachments =
      (form?.getFieldValue('refund_attachments') as
        | { id: number }[]
        | undefined) ?? [];
    setConfirmLoading(true);
    try {
      await refundConfirm(
        {
          refund_order_id: orderId,
          action: 'CONFIRM',
          reject_reason: '',
          refund_remark: refundRemark ?? '',
          attachment_ids: refundAttachments.map((a) => a.id),
        },
        { silent: true }
      );
      Message.success('确认通过');
      onRefresh?.();
    } catch (err: unknown) {
      // 确认失败必须提示（原实现无 catch：接口失败被 finally 静默吞掉，用户误以为节点未流转）
      Message.error((err as { message?: string })?.message || '确认失败');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleAbandon = () => {
    if (!orderId) return;
    Modal.confirm({
      title: '确认废弃该退款单？',
      // 高于 Drawer(zIndex 1030)，避免被抽屉盖住（rock-modal 默认 1000 < drawer）
      zIndex: Z_INDEX.modal,
      onOk: async () => {
        try {
          await refundAbandon(orderId, { silent: true });
          Message.success('已废弃');
          onRefresh?.();
        } catch (err: unknown) {
          Message.error((err as { message?: string })?.message || '废弃失败');
        }
      },
    });
  };

  return (
    <>
      {canAbandon && (
        <Button danger onClick={handleAbandon} style={{ minWidth: 120 }}>
          废弃
        </Button>
      )}
      {canReturn && (
        <Button
          danger
          loading={confirmLoading}
          onClick={sendBack.open}
          style={{ minWidth: 120 }}
        >
          退回
        </Button>
      )}
      {canConfirm && (
        <Button
          type="primary"
          loading={confirmLoading}
          onClick={handleConfirm}
          style={{ minWidth: 120 }}
        >
          确认
        </Button>
      )}
      {!canConfirm && !canReturn && !canAbandon && (
        <span className="text-xs text-gray-400">当前无待办操作</span>
      )}
      {sendBack.renderModal()}
    </>
  );
};

export default RefundActions;
